import Vue from 'vue';
import cloneDeep from 'clone';
import { routes } from '@/router/index.js';
import * as auth from './../../utils/auth.js';
import * as config from '@/../config/index.js';

const state = {
  user: {
    firstName: '',
    lastName: ''
  },
  routes: cloneDeep(routes),
  role: null,
  emailVerified: null,
  availableRoles: [],
  notifications: [],
  pageError: null,
  pageLoading: false,
  sidebarExpanded: localStorage.getItem('sidebar')
    ? JSON.parse(localStorage.getItem('sidebar')).expanded
    : false,
  entryUrl: null
};

const mutations = {
  SET_USER(state, payload) {
    state.user = payload;
  },
  SET_PAGE_FAILURE(state, payload) {
    if (!payload) {
      state.pageError = null;
    } else if (payload instanceof Error && payload.response) {
      state.pageError = {
        status: `${payload.response.status}`,
        statusText: payload.response.statusText
      };
    } else {
      state.pageError = {
        status: payload.status || 'Ooops...',
        statusText: payload.statusText || this.$t('somethingWentWrong')
      };
    }
  },
  SET_NOTIFICATIONS(state, payload) {
    state.notifications = payload;
  },
  MERGE_NOTIFICATIONS(state, payload) {
    state.notifications = [...state.notifications, ...payload];
  },
  SET_PAGE_LOADING(state, payload) {
    state.pageLoading = payload;
  },
  SET_USER_ROLE(state, payload) {
    state.role = payload;
  },
  SET_AVAILABLE_ROLES(state, roles) {
    if (Array.isArray(roles)) {
      // state.availableRoles = roles.map(el => el.toLowerCase());
      state.availableRoles = roles
        .map(el => {
          if (config.default.roles.get(el) === 'email-verified') return false;
          return config.default.roles.get(el);
        })
        .filter(Boolean);
    } else if (typeof roles === 'string' || roles instanceof String) {
      state.availableRoles = [roles.toLowerCase()];
    }
  },
  SET_EMAIL_VERIFIED(state, payload) {
    state.emailVerified = payload;
  },
  SET_SIDEBAR_EXPANDED(state, payload) {
    state.sidebarExpanded = payload;
    localStorage.setItem('sidebar', JSON.stringify({ expanded: payload }));
  },
  SET_ENTRY_URL(state, payload) {
    state.entryUrl = payload;
  }
};

const getters = {
  routes: state => state.routes.filter(route => !route.hidden),
  role: state => state.role,
  emailVerified: state => state.emailVerified,
  user: state => state.user,
  availableRoles: state => state.availableRoles,
  notifications: state => state.notifications,
  pageError: state => state.pageError,
  sidebarExpanded: state => state.sidebarExpanded,
  entryUrl: state => state.entryUrl,
  fullName: state => `${state.user.firstName} ${state.user.lastName}`
};

const actions = {
  logout: () => {
    Vue.api.logout();
  },
  fetchUser: ({ commit }) => {
    return Vue.api.auth
      .get('/profile')
      .then(res => {
        if (res) {
          commit('SET_USER', res);
          commit('SET_AVAILABLE_ROLES', res.roles);
          commit('SET_EMAIL_VERIFIED', res.roles.includes(7));
          auth.saveUser(res);
        }
        return res;
      })
      .catch(e => {
        commit('SET_PAGE_FAILURE', {
          status: e.status,
          statusText: e.statusText || this.$t('userProblem')
        });
        return Promise.reject(e);
      });
  },
  fetchNotifications: ({ commit }, { limit = 10, cursor }) => {
    return Vue.api.auth
      .get('/v0/notifications', {
        params: { limit, cursor }
      })
      .then(res => {
        commit('MERGE_NOTIFICATIONS', res.list);
        return res;
      });
  },
  switchRole: ({ state, commit }, role) => {
    if (state.availableRoles.includes(role)) {
      commit('SET_USER_ROLE', role);
      auth.saveActiveRole(role);
    }

    if (
      typeof role === 'number' &&
      state.availableRoles.includes(config.default.roles.get(role))
    ) {
      const userRole = config.default.roles.get(role);
      commit('SET_USER_ROLE', userRole);
      auth.saveActiveRole(userRole);
    }
  },
  changeSidebarState: ({ commit }, expanded) => {
    commit('SET_SIDEBAR_EXPANDED', expanded);
  }
};

export default {
  state,
  mutations,
  getters,
  actions
};
